import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, Upload, Search, Briefcase, ListChecks, Sparkles, FileText,
  Mic2, DollarSign, CalendarDays, Zap, Bot, FileCode, Brain, Target, LifeBuoy,
  Activity, ChevronDown, Crown, Users
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const navMain = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/upload-resume", label: "Upload Resume", icon: Upload, testid: "nav-upload-resume" },
  { to: "/job-search", label: "Job Search", icon: Search, testid: "nav-job-search" },
  { to: "/job-matches", label: "Job Matches", icon: Briefcase, testid: "nav-job-matches" },
  { to: "/tailored-resume", label: "Tailored Resume", icon: FileCode, testid: "nav-tailored-resume" },
  { to: "/applications", label: "Applications Tracker", icon: ListChecks, testid: "nav-applications" },
  { to: "/ats-optimizer", label: "ATS Optimizer", icon: Sparkles, testid: "nav-ats" },
  { to: "/cover-letter", label: "Cover Letter Generator", icon: FileText, testid: "nav-cover-letter" },
  { to: "/interview-prep", label: "Interview Prep", icon: Mic2, testid: "nav-interview" },
  { to: "/salary-insights", label: "Salary Insights", icon: DollarSign, testid: "nav-salary" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/auto-apply", label: "Auto Apply (Beta)", icon: Zap, testid: "nav-auto-apply" },
  { to: "/contact", label: "Customer Support", icon: LifeBuoy, testid: "nav-support" },
];

const navAgents = [
  { to: "/agents/browser", label: "Browser Agent", icon: Bot, testid: "nav-browser-agent" },
  { to: "/agents/cv-parser", label: "CV Parser Agent", icon: FileCode, testid: "nav-cv-parser" },
  { to: "/agents/jd-analyzer", label: "JD Analyzer Agent", icon: Brain, testid: "nav-jd-analyzer" },
  { to: "/agents/job-match", label: "Job Match Agent", icon: Target, testid: "nav-job-match-agent" },
  { to: "/agents/resume-tailor", label: "Resume Tailor Agent", icon: Sparkles, testid: "nav-tailor-agent" },
  { to: "/agents/tracker", label: "Tracker Agent", icon: Activity, testid: "nav-tracker-agent" },
];

const linkClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
    isActive
      ? "bg-violet-600/25 text-violet-200 border border-violet-500/40"
      : "text-zinc-400 hover:text-zinc-100 hover:bg-white/5 border border-transparent"
  }`;

export default function Sidebar({ mobileOpen = false, onClose = () => {} }) {
  const { user } = useAuth();
  const strength = user?.profile_strength ?? 78;

  return (
    <aside
      data-testid="sidebar"
      className={`w-72 shrink-0 h-screen overflow-y-auto border-r border-violet-500/10 bg-[#0c0c20] lg:bg-[#0c0c20]/90 backdrop-blur ${
        mobileOpen ? "fixed inset-y-0 left-0 z-50" : "hidden"
      } lg:block lg:sticky lg:top-0 lg:z-auto`}
    >
      <div className="px-5 py-6">
        {/* Brand */}
        <div className="flex items-center gap-3 mb-8" data-testid="brand">
          <img src="/logo.png" alt="Curcompass logo" className="w-10 h-10 rounded-xl object-cover" />
          <div>
            <div className="font-bold text-lg text-white leading-none">Curcompass</div>
            <div className="text-[10px] tracking-[0.18em] text-violet-300/80 mt-1">AI CAREER COPILOT</div>
          </div>
        </div>

        {/* Main nav */}
        <nav className="space-y-1">
          {navMain.map((n) => (
            <NavLink key={n.to} to={n.to} className={linkClass} data-testid={n.testid} onClick={onClose}>
              <n.icon className="w-4 h-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Tools & Agents - hidden from end users; agents work behind the scenes */}
        {false && (
          <>
            <div className="mt-8 mb-3 px-3 text-[10px] tracking-[0.2em] text-zinc-500 font-semibold">
              TOOLS & AGENTS
            </div>
            <nav className="space-y-1">
              {navAgents.map((n) => (
                <NavLink key={n.to} to={n.to} className={linkClass} data-testid={n.testid}>
                  <n.icon className="w-4 h-4" />
                  <span>{n.label}</span>
                </NavLink>
              ))}
            </nav>
          </>
        )}

        {/* Upgrade card */}
        {process.env.REACT_APP_FREE_MODE !== "true" && user?.plan !== "Premium" && (
          <div className="mt-8 jr-card" data-testid="upgrade-card" style={{ background: "linear-gradient(135deg, rgba(139,92,246,0.15), rgba(34,211,238,0.05))" }}>
            <div className="flex items-center gap-2 text-violet-200 font-semibold text-sm mb-2">
              <Crown className="w-4 h-4" /> Go Premium
            </div>
            <p className="text-xs text-zinc-400 mb-3">Unlimited searches, AI tailoring, cover letters &amp; auto-apply — ₹399/mo.</p>
            <NavLink to="/subscribe" onClick={onClose} className="jr-btn-primary w-full text-sm py-2 block text-center" data-testid="upgrade-now-btn">Upgrade Now</NavLink>
          </div>
        )}

        {/* User profile */}
        {user && (
          <div className="mt-6 pt-5 border-t border-white/5" data-testid="user-profile-card">
            <div className="flex items-center gap-3">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-cyan-400 grid place-items-center text-white text-sm font-semibold">
                  {(user.name || user.email || "U").slice(0, 2).toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <div className="text-sm font-medium text-white truncate" data-testid="user-name">{user.name || "User"}</div>
                <div className="text-[11px] text-zinc-400">{user.plan || "Free"} Plan</div>
              </div>
            </div>
            <div className="mt-3">
              <div className="flex justify-between text-[11px] text-zinc-400 mb-1">
                <span>{strength}% profile strength</span>
              </div>
              <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400"
                  style={{ width: `${strength}%` }}
                />
              </div>
            </div>
            <NavLink to="/profile" onClick={onClose} className="mt-3 block text-center jr-btn-ghost text-xs py-2" data-testid="view-profile-btn">
              View Profile
            </NavLink>
          </div>
        )}
      </div>
    </aside>
  );
}
